<template>
  <div class="pa-4">
    <v-expansion-panels v-model="expandedPanels" multiple>
      <!-- Panel 1: Generate Spreadsheet Template -->
      <v-expansion-panel value="template">
        <v-expansion-panel-title>
          <v-icon class="mr-2">mdi-file-excel</v-icon>
          Step 1: Generate Spreadsheet Template
          <v-chip size="x-small" variant="outlined" class="ml-2">Optional</v-chip>
        </v-expansion-panel-title>

        <v-expansion-panel-text class="pa-6">
          <div class="text-subtitle-2 text-medium-emphasis mb-4">
            Generate a blank spreadsheet template to define your task set profile structure offline
          </div>

          <!-- Profile Details (Optional) -->
          <div class="mb-4">
            <div class="text-subtitle-1 mb-2">Profile Details (Optional):</div>
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="templateConfig.code"
                  label="Code"
                  variant="outlined"
                  density="comfortable"
                  hint="Unique identifier (e.g., TSP001)"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="templateConfig.title"
                  label="Title"
                  variant="outlined"
                  density="comfortable"
                  hint="Descriptive name for the profile"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </div>

          <!-- Structure Options -->
          <div class="mb-4">
            <div class="text-subtitle-1 mb-2">Structure:</div>
            <v-text-field
              v-model.number="templateConfig.taskLevels"
              type="number"
              :min="1"
              :max="10"
              label="Task Levels"
              hint="Number of nested task level columns"
              persistent-hint
              variant="outlined"
              density="comfortable"
              class="mt-2"
              style="max-width: 200px;"
            />
          </div>

          <!-- Include Options -->
          <div class="mb-4">
            <div class="text-subtitle-1 mb-2">Include:</div>
            <v-checkbox
              v-model="templateConfig.includeWeighting"
              label="Weighting"
              density="compact"
              hide-details
            />
            <v-checkbox
              v-model="templateConfig.includeLeadTeam"
              label="Lead Team"
              density="compact"
              hide-details
              class="mt-2"
            />
            <v-checkbox
              v-model="templateConfig.includeVersionReferences"
              density="compact"
              hide-details
              class="mt-2"
            >
              <template #label>
                <div>
                  <div>Version References (Task & Subtask)</div>
                  <div class="text-caption text-medium-emphasis">
                    If unchecked, references will be auto-generated
                  </div>
                </div>
              </template>
            </v-checkbox>
          </div>

          <div class="d-flex justify-end mt-4">
            <v-btn
              color="success"
              variant="flat"
              size="large"
              prepend-icon="mdi-download"
              :loading="downloadingTemplate"
              @click="downloadTemplate"
            >
              Download Template
            </v-btn>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Panel 2: Import from Excel -->
      <v-expansion-panel value="import">
        <v-expansion-panel-title>
          <v-icon class="mr-2">mdi-upload</v-icon>
          Step 2: Create Profile from Excel
        </v-expansion-panel-title>

        <v-expansion-panel-text class="pa-6">
          <div class="text-subtitle-2 text-medium-emphasis mb-4">
            Upload your completed spreadsheet to create new profiles. Each sheet becomes a separate profile.
          </div>

          <v-file-input
            ref="fileInputRef"
            accept=".xlsx,.xls"
            label="Choose Excel file"
            variant="outlined"
            density="comfortable"
            prepend-icon="mdi-file-excel"
            hint="Upload your completed spreadsheet to create profiles"
            persistent-hint
            clearable
            :loading="importing"
            :disabled="importing"
            @update:model-value="handleFileUpload"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Import Review Dialog -->
    <v-dialog v-model="importDialogOpen" max-width="800" persistent>
      <v-card v-if="importResult">
        <v-card-title class="text-h6 px-4 pt-4">Review New Profiles</v-card-title>
        <v-card-text>
          <v-alert v-if="importResult.errors.length" type="error" class="mb-4">
            <div class="font-weight-bold mb-2">Errors (must fix):</div>
            <div v-for="(err, idx) in importResult.errors" :key="idx" class="text-body-2">
              <template v-if="err.sheet">{{ err.sheet }}</template>
              <template v-if="err.row"> Row {{ err.row }}</template>
              <template v-if="err.column"> [{{ err.column }}]</template>
              : {{ err.message }}
            </div>
          </v-alert>

          <v-alert v-if="importResult.warnings.length" type="warning" class="mb-4">
            <div class="font-weight-bold mb-2">Warnings:</div>
            <div v-for="(warn, idx) in importResult.warnings" :key="idx" class="text-body-2">
              <template v-if="warn.sheet">{{ warn.sheet }}</template>
              <template v-if="warn.row"> Row {{ warn.row }}</template>
              : {{ warn.message }}
            </div>
          </v-alert>

          <v-alert v-if="importResult.isValid && profilesToCreate.length > 0" type="success" class="mb-4">
            Ready to create {{ profilesToCreate.length }} new profile(s)
            <template v-if="existingProfiles.length > 0">
              <br />
              {{ existingProfiles.length }} profile(s) already exist (use Update page to modify)
            </template>
          </v-alert>

          <v-list v-if="importResult.profiles.length > 0" density="compact">
            <v-list-subheader>New Profiles to Create</v-list-subheader>
            <v-list-item v-for="profile in profilesToCreate" :key="profile.code">
              <template #prepend>
                <v-icon color="success">mdi-plus-circle</v-icon>
              </template>
              <v-list-item-title>{{ profile.code }} | {{ profile.title }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ profile.taskProfiles?.length || 0 }} tasks
              </v-list-item-subtitle>
            </v-list-item>

            <template v-if="existingProfiles.length > 0">
              <v-list-subheader class="mt-2">Already Existing (skipped)</v-list-subheader>
              <v-list-item v-for="profile in existingProfiles" :key="profile.code">
                <template #prepend>
                  <v-icon color="warning">mdi-alert-circle-outline</v-icon>
                </template>
                <v-list-item-title>{{ profile.code }} | {{ profile.title }}</v-list-item-title>
                <v-list-item-subtitle>
                  Use Update page to modify existing profiles
                </v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-list>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn color="grey" @click="cancelImport">Cancel</v-btn>
          <v-btn
            color="primary"
            :disabled="!importResult.isValid || profilesToCreate.length === 0"
            :loading="creatingFromImport"
            @click="confirmImport"
          >
            Create All ({{ profilesToCreate.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar v-model="showSuccessSnackbar" :timeout="3000" color="success">
      {{ successMessage }}
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showErrorSnackbar" :timeout="5000" color="error">
      {{ errorMessage }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useAppStore } from '@/stores/app';
import { useSettingsStore } from '@/stores/settings';
import { useRouter } from 'vue-router';
import TaskSetProfileToSpreadsheetConverter from '@/utils/TaskSetProfileToSpreadsheetConverter';
import { TaskSetProfileExcelProcessor } from '@/utils/TaskSetProfileExcelProcessor';

const appStore = useAppStore();
const settingsStore = useSettingsStore();
const router = useRouter();

// State
const downloadingTemplate = ref(false);
const importing = ref(false);
const creatingFromImport = ref(false);
const importDialogOpen = ref(false);
const importResult = ref(null);
const fileInputRef = ref(null);
const allTaskSetProfiles = ref([]);
const expandedPanels = ref(['template']);

// Template configuration
const templateConfig = ref({
  code: '',
  title: '',
  taskLevels: 5,
  includeWeighting: true,
  includeLeadTeam: true,
  includeVersionReferences: false
});

// Snackbar state
const showSuccessSnackbar = ref(false);
const successMessage = ref('');
const showErrorSnackbar = ref(false);
const errorMessage = ref('');

// Computed
const projectCode = computed(() => settingsStore.getProjectCode);

const profilesToCreate = computed(() => {
  if (!importResult.value?.profiles) return [];
  return importResult.value.profiles.filter(profile =>
    !allTaskSetProfiles.value.some(tsp => tsp.code === profile.code)
  );
});

const existingProfiles = computed(() => {
  if (!importResult.value?.profiles) return [];
  return importResult.value.profiles.filter(profile =>
    allTaskSetProfiles.value.some(tsp => tsp.code === profile.code)
  );
});

// Watch taskLevels to enforce min/max
watch(() => templateConfig.value.taskLevels, (newValue) => {
  if (newValue < 1) {
    templateConfig.value.taskLevels = 1;
  } else if (newValue > 10) {
    templateConfig.value.taskLevels = 10;
  }
});

// Methods
async function loadProfiles() {
  try {
    const profiles = await appStore.apiClient.get(
      `/api/${projectCode.value}/task-set-profiles`
    );
    allTaskSetProfiles.value = profiles.map(p => ({
      title: `${p.code} | ${p.title}`,
      code: p.code,
      profile: p
    }));
  } catch (error) {
    console.error('Error loading profiles:', error);
  }
}

async function downloadTemplate() {
  downloadingTemplate.value = true;
  try {
    // Fetch reference data for template
    const [teams, issueTemplates, processTemplates] = await Promise.all([
      appStore.apiClient.get(`/api/${projectCode.value}/teams`),
      appStore.apiClient.get('/api/issue-templates?published=true'),
      appStore.apiClient.get('/api/process-templates?published=true')
    ]);

    const templates = [
      ...issueTemplates.map(t => ({ ...t, type: 'issue' })),
      ...processTemplates.map(t => ({ ...t, type: 'process' }))
    ];

    // Create example profile template with configured task levels
    const profileCode = templateConfig.value.code || 'NEW_PROFILE';
    const profileTitle = templateConfig.value.title || 'New Profile Template';

    // Build example task structure based on task levels
    const buildExampleChildren = (depth, maxDepth) => {
      if (depth >= maxDepth) return [];
      return [{
        versionReference: templateConfig.value.includeVersionReferences ? '' : undefined,
        code: `1.${'1.'.repeat(depth)}1`,
        title: `Example Level ${depth + 2} Task`,
        taskCodes: [],
        subtaskProfiles: [],
        children: buildExampleChildren(depth + 1, maxDepth)
      }];
    };

    const exampleProfile = {
      code: profileCode,
      title: profileTitle,
      taskProfiles: [
        {
          versionReference: templateConfig.value.includeVersionReferences ? '' : undefined,
          code: '1',
          title: 'Example Task 1',
          taskCodes: ['EXAMPLE'],
          subtaskProfiles: [
            {
              versionReference: templateConfig.value.includeVersionReferences ? '' : undefined,
              title: 'Example Subtask',
              weighting: templateConfig.value.includeWeighting ? 1 : undefined,
              itemType: 'process'
            }
          ],
          children: buildExampleChildren(0, Math.min(templateConfig.value.taskLevels - 1, 2))
        },
        {
          versionReference: templateConfig.value.includeVersionReferences ? '' : undefined,
          code: '2',
          title: 'Example Task 2',
          taskCodes: [],
          subtaskProfiles: [],
          children: []
        }
      ]
    };

    const converterOptions = {
      taskLevels: templateConfig.value.taskLevels,
      includeTaskCodes: true,
      includeLeadTeam: templateConfig.value.includeLeadTeam,
      includeWeighting: templateConfig.value.includeWeighting,
      includeVersionReferences: templateConfig.value.includeVersionReferences
    };

    const converter = new TaskSetProfileToSpreadsheetConverter();
    const buffer = await converter.process([exampleProfile], teams, templates, converterOptions);

    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${profileCode}-template.xlsx`;
    link.click();
    URL.revokeObjectURL(url);

    successMessage.value = 'Template downloaded';
    showSuccessSnackbar.value = true;
  } catch (error) {
    console.error('Error downloading template:', error);
    errorMessage.value = 'Failed to download template';
    showErrorSnackbar.value = true;
  } finally {
    downloadingTemplate.value = false;
  }
}

async function handleFileUpload(files) {
  const file = Array.isArray(files) ? files[0] : files;
  if (!file) return;

  importing.value = true;
  try {
    const buffer = await file.arrayBuffer();
    const processor = new TaskSetProfileExcelProcessor();
    importResult.value = processor.processFile(buffer);
    importDialogOpen.value = true;
  } catch (error) {
    console.error('Error processing file:', error);
    errorMessage.value = 'Failed to process Excel file';
    showErrorSnackbar.value = true;
  } finally {
    importing.value = false;
    if (fileInputRef.value) {
      fileInputRef.value.reset();
    }
  }
}

function cancelImport() {
  importDialogOpen.value = false;
  importResult.value = null;
}

async function confirmImport() {
  if (!importResult.value?.isValid || profilesToCreate.value.length === 0) return;

  creatingFromImport.value = true;
  let createdCount = 0;
  const errors = [];

  try {
    for (const profile of profilesToCreate.value) {
      try {
        // Prepare profile for API
        const createPayload = {
          ...profile,
          key: '',
          isPublished: false,
          current: true,
          publishMessage: ''
        };

        await appStore.apiClient.post(
          `/api/${projectCode.value}/task-set-profiles`,
          createPayload
        );
        createdCount++;
      } catch (error) {
        console.error(`Error creating profile ${profile.code}:`, error);
        errors.push(`${profile.code}: ${error.message || 'Creation failed'}`);
      }
    }

    await loadProfiles();
    importDialogOpen.value = false;
    importResult.value = null;

    if (errors.length > 0) {
      errorMessage.value = `Created ${createdCount} profile(s). Errors: ${errors.join(', ')}`;
      showErrorSnackbar.value = true;
    } else {
      successMessage.value = `Successfully created ${createdCount} profile(s)`;
      showSuccessSnackbar.value = true;

      // Navigate to update page to see new profiles
      router.push({ name: 'TSPUpdate' });
    }
  } catch (error) {
    console.error('Error during import:', error);
    errorMessage.value = 'Failed to complete import';
    showErrorSnackbar.value = true;
  } finally {
    creatingFromImport.value = false;
  }
}

onMounted(loadProfiles);
</script>
